import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { creatorbase, creatorbaseQueries } from '@round/api';
import { useCampaign } from '@round/creatorbase-helpers/api-hooks';

type Config = {
    enabled?: boolean;
};

export default function useCampaignAllocations(campaignId: number | null | undefined, config?: Config) {
    const campaignQuery = useCampaign(campaignId, { staleTime: 0, ...config });
    const allocationsQuery = useAllocations(campaignId, config);

    return {
        campaign: campaignQuery.data,
        isCampaignLoading: campaignQuery.isLoading,
        campaignErrorMessage: campaignQuery.errorMessage,
        ...allocationsQuery,
        refresh: () => {
            allocationsQuery.refresh();
            campaignQuery.refetch();
        },
    };
}

const poAllocationOptions = creatorbaseQueries.purchaseOrderLines.options.infinite;
const poAllocationUtils = creatorbaseQueries.purchaseOrderLines.utils.list;
const poOptions = creatorbaseQueries.purchaseOrders.options.list;
const poUtils = creatorbaseQueries.purchaseOrders.utils.list;

export type CampaignPOAllocation = creatorbase.PurchaseOrderLine & {
    purchaseOrder: creatorbase.PurchaseOrder | null;
};

function useAllocations(campaignId: number | null | undefined, config?: Config) {
    const poAllocationsQuery = useInfiniteQuery({
        ...poAllocationOptions({ campaign_id: campaignId?.toString() }),
        enabled: !!campaignId && config?.enabled,
        staleTime: 0,
    });

    const poIds = poAllocationsQuery.data?.pages.flatMap((page) => page.results.map((line) => line.purchase_order_id));
    const poQuery = useQuery({
        ...poOptions({ id: poIds?.toString(), page_size: poIds?.length, page: 1 }),
        enabled: !!poIds?.length,
    });

    const allocations: CampaignPOAllocation[] =
        poAllocationsQuery.data?.pages.flatMap((page) =>
            page.results.map((line) => {
                const po = poQuery.data?.results.find((po) => po.id === line.purchase_order_id);
                return {
                    ...line,
                    purchaseOrder: po ?? null,
                };
            })
        ) ?? [];

    const allocationsErrorMessage = !poAllocationsQuery.error
        ? null
        : poAllocationUtils.isExpectedErrorResponse(poAllocationsQuery.error)
          ? poAllocationsQuery.error.response.data.detail
          : poAllocationsQuery.error.message;

    const poErrorMessage = !poQuery.error
        ? null
        : poUtils.isExpectedErrorResponse(poQuery.error)
          ? poQuery.error.response.data.detail
          : poQuery.error.message;

    return {
        allocations,
        areAllocationsLoading: poAllocationsQuery.isFetching || poQuery.isFetching,
        allocationsErrorMessage: allocationsErrorMessage || poErrorMessage,
        refresh: () => {
            poAllocationsQuery.refetch();
            poQuery.refetch();
        },
        hasNextPage: poAllocationsQuery.hasNextPage,
        fetchNextPage: poAllocationsQuery.fetchNextPage,
    };
}
